<template>
  <VcLayout column class="calls-widget pa-6" :class="{ 'calls-widget--loaded': isWidgetReady }">
    <!-- Screen 1: When data is available -->
    <div v-if="hasData && !isLoading" class="calls-widget__data-screen">
      <div class="calls-widget__title">
        {{ $t('widget.calls_widget.title') }}
      </div>
      <VcLayout class="calls-widget__wrapper mt-4">
        <VcLayout class="calls-widget__data">
          <CallsWidgetContent
            :is-loading-stats="false"
            :is-loading-calls="false"
            :call-stats="callStats"
            :recent-calls="recentCalls"
            :total-calls="totalCalls"
            :missed-calls-pending="missedCallsPending"
            @navigate="navigate"
            @click-empty-state="goToCallsSettings"
          />
        </VcLayout>
      </VcLayout>
    </div>

    <!-- Screen 2: When no data is available -->
    <VcLayout v-else-if="!hasData && !isLoading" column class="calls-widget__empty-screen">
      <div class="calls-widget__title">
        {{ $t('widget.calls_widget.title') }}
      </div>
      <VcLayout class="calls-widget__empty-wrapper mt-4">
        <div class="calls-widget__empty-container">
          <VcEmptyState
            :subtitle="$t('widget.calls_widget.empty_state.no_calls_title')"
            title=""
            :button="$t('widget.calls_widget.empty_state.manage_preferences')"
            :image="require('@/assets/images/widgets/calls-empty-phone.svg')"
            outlined-button
            size="md"
            @click="goToCallsSettings"
          />
        </div>
      </VcLayout>
    </VcLayout>

    <!-- Loading Screen -->
    <div v-else class="calls-widget__loading-screen">
      <div class="calls-widget__title">{{ $t('widget.calls_widget.title') }}</div>
      <VcLayout class="calls-widget__wrapper mt-4">
        <VcLayout class="calls-widget__data">
          <CallsWidgetContent
            :is-loading-stats="true"
            :is-loading-calls="true"
            :call-stats="null"
            :recent-calls="null"
            @navigate="navigate"
            @click-empty-state="goToCallsSettings"
          />
        </VcLayout>
      </VcLayout>
    </div>
  </VcLayout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import {
  getStartOfWeek,
  getStartOfMonth,
  getStartOfYear,
} from '@/modules/common/utils/dateUtils';
import CallsWidgetContent from './CallsWidgetContent.vue';

export default {
  name: 'CallsWidget',
  components: {
    CallsWidgetContent,
  },
  data() {
    return {
      isWidgetReady: false,
    };
  },
  props: {
    appCodeName: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapGetters('CallsWidgetStore', [
      'callStats',
      'recentCalls',
      'isLoadingStats',
      'isLoadingCalls',
      'isLoadingMissedCallsCount',
      'totalCalls',
      'missedCallsPending',
    ]),
    isLoading() {
      return this.isLoadingStats || this.isLoadingCalls || this.isLoadingMissedCallsCount;
    },
    hasData() {
      const hasStats = this.callStats && (
        (this.callStats.total_voice_calls && this.callStats.total_voice_calls > 0)
        || (this.callStats.status_counts && this.callStats.status_counts.length > 0)
      );

      const hasCalls = this.recentCalls && Array.isArray(this.recentCalls) && this.recentCalls.length > 0;

      return hasStats || hasCalls;
    },
    callsRoute() {
      return '/app/crm/calls';
    },
    timeRanges() {
      const currentUtcTime = new Date();
      return {
        WEEK: {
          startDate: getStartOfWeek(),
          endDate: currentUtcTime,
        },
        MONTH: {
          startDate: getStartOfMonth(),
          endDate: currentUtcTime,
        },
        YEAR: {
          startDate: getStartOfYear(),
          endDate: currentUtcTime,
        },
      };
    },
  },
  async created() {
    this.addI18nModule('widgets-pov');
    await this.init();
    this.$emit('track', 'View');
    this.$emit('ready');
    this.isWidgetReady = true;
  },
  methods: {
    ...mapActions('CallsWidgetStore', ['fetchCallStats', 'fetchRecentCalls', 'fetchMissedCallsClientCount']),
    async init() {
      // Calculate 30 days ago for the API call
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const timeRange = {
        startDate: thirtyDaysAgo.toISOString().split('T')[0], // Only date part (YYYY-MM-DD)
        endDate: new Date().toISOString().split('T')[0], // Only date part (YYYY-MM-DD)
      };

      await Promise.all([
        this.fetchCallStats(timeRange),
        this.fetchRecentCalls({ perPage: 4 }),
        this.fetchMissedCallsClientCount(timeRange),
      ]);
    },
    navigate({ path, queryParams }) {
      this.$emit('track', 'Call Item Click');
      this.$emit('sdkAction', 'Navigate', {
        type: 'relative',
        path,
        queryParams,
      });
    },
    goToCallsSettings() {
      this.$emit('track', 'Empty State Click');
      this.$emit('sdkAction', 'Navigate', {
        type: 'relative',
        path: `/app_market/calls_and_texting/settings?appCodeName=${this.appCodeName}`,
      });
    },
  },
};
</script>

<style scoped lang="scss">
$xSmall: 505px;
$small: 598px;
$medium: 738px;
$large: 859px;
$xLarge: 1031px;

.calls-widget {
  container-type: inline-size;
  color: var(--gray-darken-5);
  text-align: start;
  width: 100%;
  //min-height: 100%;

  &__title {
    width: 100%;
    font-size: var(--font-size-medium2);
    font-weight: var(--font-weight-large2);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__wrapper {
    height: 100%;
  }

  &__data {
    flex-grow: 0;
    flex-shrink: 1;
    width: 100%;
    height: 100%;
  }

  &__data-screen {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  &__empty-container {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    ::v-deep {
      .VcEmptyState {
        text-align: center;
        .VcEmptyStateImage {
          margin: 0 auto;
          width: 54px;
          height: 74px;
        }
      }
    }
  }

  &__loading-screen {
    width: 100%;
    height: 100%;
  }
}
</style>
